import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, MapPin, Clock, Edit, Trash2, Loader } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ScheduledRides = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRide, setEditingRide] = useState(null);
  const [editForm, setEditForm] = useState({
    scheduledDate: '',
    scheduledTime: '',
    pickup: '',
    dropoff: ''
  });

  useEffect(() => {
    fetchScheduledRides();
  }, []);

  const fetchScheduledRides = async () => {
    try {
      const api = (await import('../utils/api')).default;
      const response = await api.get('/scheduled-rides/my-scheduled-rides');
      setRides(response.data.rides || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching scheduled rides:', error);
      toast({
        title: 'Error',
        description: 'Failed to load scheduled rides',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleEdit = (ride) => {
    setEditingRide(ride);
    
    // Parse scheduled time
    const scheduledDate = new Date(ride.scheduledAt);
    const dateStr = scheduledDate.toISOString().split('T')[0];
    const timeStr = scheduledDate.toTimeString().slice(0, 5);
    
    setEditForm({
      scheduledDate: dateStr,
      scheduledTime: timeStr,
      pickup: ride.pickup,
      dropoff: ride.dropoff
    });
    
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      // Validate
      if (!editForm.scheduledDate || !editForm.scheduledTime) {
        toast({
          title: 'Error',
          description: 'Please select date and time',
          variant: 'destructive',
        });
        return;
      }

      // Combine date and time
      const scheduledAt = new Date(`${editForm.scheduledDate}T${editForm.scheduledTime}`);
      
      // Validate future time
      if (scheduledAt <= new Date()) {
        toast({
          title: 'Error',
          description: 'Scheduled time must be in the future',
          variant: 'destructive',
        });
        return;
      }

      const api = (await import('../utils/api')).default;
      await api.put(`/scheduled-rides/${editingRide.id}`, {
        scheduledAt: scheduledAt.toISOString(),
        pickup: editForm.pickup,
        dropoff: editForm.dropoff
      });

      toast({
        title: 'Success',
        description: 'Scheduled ride updated successfully',
      });

      setShowEditModal(false);
      fetchScheduledRides();
    } catch (error) {
      console.error('Error updating ride:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update ride',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = async (rideId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled ride?')) {
      return;
    }

    try {
      const api = (await import('../utils/api')).default;
      await api.delete(`/scheduled-rides/${rideId}`);

      toast({
        title: 'Success',
        description: 'Scheduled ride cancelled',
      });

      fetchScheduledRides();
    } catch (error) {
      console.error('Error cancelling ride:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel ride',
        variant: 'destructive',
      });
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    };
  };

  const getTimeUntil = (dateString) => {
    const scheduledDate = new Date(dateString);
    const now = new Date();
    const diff = scheduledDate - now;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours < 1) {
      return `in ${minutes} minutes`;
    } else if (hours < 24) {
      return `in ${hours} hours`;
    } else {
      const days = Math.floor(hours / 24);
      return `in ${days} days`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-pink-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Scheduled Rides</h1>
            <p className="text-gray-600">Manage your upcoming rides</p>
          </div>
          <Button
            onClick={() => navigate('/')}
            className="bg-pink-600 hover:bg-pink-700"
          >
            Schedule New Ride
          </Button>
        </div>

        {rides.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Scheduled Rides</h2>
            <p className="text-gray-600 mb-4">
              You don't have any scheduled rides yet
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-pink-600 hover:bg-pink-700"
            >
              Schedule a Ride
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride) => {
              const { date, time } = formatDateTime(ride.scheduledAt);
              const timeUntil = getTimeUntil(ride.scheduledAt);
              
              return (
                <Card key={ride.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-4">
                      {/* Date & Time */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-pink-600" />
                          <span className="font-semibold text-gray-900">{date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-pink-600" />
                          <span className="font-semibold text-gray-900">{time}</span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">
                          {timeUntil}
                        </Badge>
                      </div>

                      {/* Route */}
                      <div className="space-y-2">
                        <div className="flex items-start gap-3">
                          <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5"></div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-600">Pickup</p>
                            <p className="font-medium text-gray-900">{ride.pickup}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5"></div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-600">Dropoff</p>
                            <p className="font-medium text-gray-900">{ride.dropoff}</p>
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="flex items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">Price:</span>
                          <span className="font-semibold text-green-600">K{ride.price}</span>
                        </div>
                        <Badge variant="outline">{ride.rideTypeId || 'Standard'}</Badge>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        onClick={() => handleEdit(ride)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleCancel(ride.id)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Scheduled Ride</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="scheduledDate">Date</Label>
                  <Input
                    id="scheduledDate"
                    type="date"
                    value={editForm.scheduledDate}
                    onChange={(e) => setEditForm({ ...editForm, scheduledDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scheduledTime">Time</Label>
                  <Input
                    id="scheduledTime"
                    type="time"
                    value={editForm.scheduledTime}
                    onChange={(e) => setEditForm({ ...editForm, scheduledTime: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="pickup">Pickup Location</Label>
                <Input
                  id="pickup"
                  value={editForm.pickup}
                  onChange={(e) => setEditForm({ ...editForm, pickup: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dropoff">Dropoff Location</Label>
                <Input
                  id="dropoff"
                  value={editForm.dropoff}
                  onChange={(e) => setEditForm({ ...editForm, dropoff: e.target.value })}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  className="flex-1 bg-pink-600 hover:bg-pink-700"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ScheduledRides;
